/**
 * Room public status (availability page) — shared between frontend and backend tests.
 *
 * - Frontend: `@merlot/common/roomPublicStatus.js`
 * - Backend: mirror in `backend/src/utils/roomPublicStatus.js` (CommonJS)
 */

import defaults from './roomPublicStatusDefaults.json' assert { type: 'json' };

export const ROOM_PUBLIC_STATUSES = defaults.publicStatuses;
export const DEFAULT_ROOM_PUBLIC_STATUS = defaults.defaultPublicStatus;

/**
 * @param {string} yearMonth - YYYY-MM
 */
export function parseYearMonth(yearMonth) {
  if (!yearMonth || typeof yearMonth !== 'string') return null;
  const match = yearMonth.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year, month };
}

/**
 * @param {number} year
 * @param {number} month - 1–12
 */
export function toYearMonthKey(year, month) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Last calendar moment (UTC) of a YYYY-MM month.
 * @param {string} yearMonth - YYYY-MM
 */
export function lastInstantOfMonth(yearMonth) {
  const parsed = parseYearMonth(yearMonth);
  if (!parsed) return null;
  return new Date(Date.UTC(parsed.year, parsed.month, 0, 23, 59, 59, 999));
}

/**
 * Whether maintenance has expired (end month is strictly before reference month).
 * @param {{ publicStatus?: string, maintenanceEndMonth?: string | null }} room
 * @param {Date} [referenceDate]
 */
export function isMaintenanceExpired(room, referenceDate = new Date()) {
  if (!room || room.publicStatus !== 'maintenance' || !room.maintenanceEndMonth) {
    return false;
  }
  const end = lastInstantOfMonth(room.maintenanceEndMonth);
  if (!end) return false;
  const ref = new Date(referenceDate);
  ref.setUTCHours(0, 0, 0, 0);
  return ref > end;
}

/**
 * Effective maintenance flag for "today" (or reference date).
 * @param {{ publicStatus?: string, maintenanceEndMonth?: string | null }} room
 * @param {Date} [referenceDate]
 */
export function isRoomOnMaintenance(room, referenceDate = new Date()) {
  if (!room || room.publicStatus !== 'maintenance') return false;
  if (!room.maintenanceEndMonth) return true;
  return !isMaintenanceExpired(room, referenceDate);
}

/**
 * Whether a calendar month is under maintenance for public display / booking blocks.
 * Maintenance applies from the calendar start through maintenanceEndMonth (inclusive), or
 * all calendar months when no end month is set.
 *
 * @param {{ publicStatus?: string, maintenanceEndMonth?: string | null }} room
 * @param {number} year
 * @param {number} month - 1–12
 */
export function isMonthInMaintenance(room, year, month) {
  if (!room || room.publicStatus !== 'maintenance') return false;

  const monthKey = toYearMonthKey(year, month);
  if (room.maintenanceEndMonth) {
    return monthKey <= room.maintenanceEndMonth;
  }
  return true;
}

/**
 * @param {string} checkIn - YYYY-MM-DD
 * @param {string} checkOut - YYYY-MM-DD
 */
export function eachMonthInDateRange(checkIn, checkOut) {
  const startParts = checkIn.split('-').map(Number);
  const endParts = checkOut.split('-').map(Number);
  if (startParts.length < 2 || endParts.length < 2) return [];

  let year = startParts[0];
  let month = startParts[1];
  const endYear = endParts[0];
  const endMonth = endParts[1];
  const months = [];

  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push({ year, month });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

/**
 * @param {{ publicStatus?: string, maintenanceEndMonth?: string | null }} room
 * @param {string} checkIn - YYYY-MM-DD
 * @param {string} checkOut - YYYY-MM-DD
 */
export function doesDateRangeOverlapMaintenance(room, checkIn, checkOut) {
  if (!room || room.publicStatus !== 'maintenance') return false;

  const months = eachMonthInDateRange(checkIn, checkOut);
  return months.some(({ year, month }) => isMonthInMaintenance(room, year, month));
}

function formatDateOnlyUTC(date) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Omit booking segments that fall in maintenance months for the public availability API.
 * @param {{ publicStatus?: string, maintenanceEndMonth?: string | null }} room
 * @param {Array<{ startDate: Date|string, endDate: Date|string }>} bookings
 */
export function clipBookingsForPublicAvailability(room, bookings) {
  const list = Array.isArray(bookings) ? bookings : [];
  if (!room || room.publicStatus !== 'maintenance') {
    return list.map((b) => ({ startDate: b.startDate, endDate: b.endDate }));
  }

  const result = [];
  for (const booking of list) {
    if (!booking?.startDate || !booking?.endDate) continue;

    const bookingStart = new Date(booking.startDate);
    const bookingEnd = new Date(booking.endDate);
    if (Number.isNaN(bookingStart.getTime()) || Number.isNaN(bookingEnd.getTime())) continue;

    const months = eachMonthInDateRange(
      formatDateOnlyUTC(bookingStart),
      formatDateOnlyUTC(bookingEnd)
    );

    let open = null;
    for (const { year, month } of months) {
      const monthStart = new Date(Date.UTC(year, month - 1, 1));
      const monthEnd = new Date(Date.UTC(year, month, 0, 23, 59, 59, 999));
      const inMaint = isMonthInMaintenance(room, year, month);

      if (!inMaint) {
        const segStart = open
          ? open.start
          : new Date(Math.max(bookingStart.getTime(), monthStart.getTime()));
        open = {
          start: segStart,
          end: new Date(Math.min(bookingEnd.getTime(), monthEnd.getTime()))
        };
      } else if (open) {
        result.push({ startDate: open.start, endDate: open.end });
        open = null;
      }
    }
    if (open) {
      result.push({ startDate: open.start, endDate: open.end });
    }
  }

  return result;
}
